import React, { useState } from 'react';
import { ArrowLeft, Plus, Pencil, Trash2, Search, Package } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useCatalog } from '@/hooks/useCatalog';
import { useUserRole } from '@/hooks/useUserRole';
import { useUserPermissions } from '@/hooks/useUserPermissions';
import { toast } from 'sonner';
import CatalogItemModal from '@/components/settings/CatalogItemModal';
import type { Catalog } from '@/types/db';

export default function CatalogSettings() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { items, loading, deleteCatalogItem } = useCatalog();
  const { hasRole } = useUserRole();
  const { isAdmin, isSalesManager } = useUserPermissions();
  const canManageCatalog = isAdmin || isSalesManager;
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<Catalog | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Catalog | null>(null);

  const handleOpenNew = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item: Catalog) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingItem(null);
  };

  const handleDeleteClick = (item: Catalog) => {
    setItemToDelete(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;

    try {
      await deleteCatalogItem(itemToDelete.id);
      toast.success(t('catalog.deleteSuccess'));
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    } catch (error: any) {
      console.error('Error deleting item:', error);
      
      // Check if it's a foreign key constraint error
      if (error?.code === '23503' || error?.message?.includes('foreign key constraint')) {
        toast.error(t('catalog.cannotDeleteInUse'), {
          duration: 5000
        });
      } else {
        toast.error(t('catalog.deleteError'));
      }
      
      setDeleteDialogOpen(false);
      setItemToDelete(null);
    }
  };

  const formatPrice = (price: number | null) => {
    if (price === null) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const getTypeBadge = (type: string) => {
    const variants: Record<string, { labelKey: string; variant: 'default' | 'secondary' }> = {
      case: { labelKey: 'catalog.case', variant: 'default' },
      service: { labelKey: 'catalog.service', variant: 'secondary' }
    };
    const config = variants[type] || { labelKey: type, variant: 'default' };
    return <Badge variant={config.variant}>{t(config.labelKey)}</Badge>;
  };

  const filteredItems = items.filter(item =>
    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/settings')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-3xl font-bold">{t('catalog.title')}</h1>
          <p className="text-muted-foreground">{t('catalog.description')}</p>
        </div>
        {canManageCatalog && (
          <Button onClick={handleOpenNew}>
            <Plus className="h-4 w-4 mr-2" />
            {t('catalog.newItem')}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={t('catalog.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">{t('catalog.noItemsFound')}</h3>
              <p className="text-muted-foreground mb-4">
                {searchQuery ? t('catalog.tryAdjusting') : t('catalog.startByAdding')}
              </p>
              {!searchQuery && (
                <Button onClick={handleOpenNew}>
                  <Plus className="h-4 w-4 mr-2" />
                  {t('catalog.addFirstItem')}
                </Button>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('catalog.name')}</TableHead>
                  <TableHead>{t('catalog.type')}</TableHead>
                  <TableHead>{t('catalog.basePrice')}</TableHead>
                  <TableHead>{t('catalog.dependentPrice')}</TableHead>
                  <TableHead>{t('catalog.status')}</TableHead>
                  <TableHead className="text-right">{t('catalog.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{item.name}</div>
                        {item.description && (
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {item.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(item.type)}</TableCell>
                    <TableCell>{formatPrice(item.base_price)}</TableCell>
                    <TableCell>
                      {item.allows_dependents ? formatPrice(item.dependent_price) : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={item.is_active ? 'default' : 'secondary'}>
                        {item.is_active ? t('catalog.active') : t('catalog.inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {canManageCatalog && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(item)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canManageCatalog && (
                           <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteClick(item)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CatalogItemModal
        isOpen={showModal}
        onClose={handleCloseModal}
        editItem={editingItem}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('catalog.confirmDeletion')}</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>{t('catalog.confirmDeleteMessage', { name: itemToDelete?.name })}</p>
              <p className="text-amber-600 font-medium">
                {t('catalog.deleteWarning')}
              </p>
              <p className="text-sm text-muted-foreground">{t('catalog.cannotBeUndone')}</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('catalog.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              {t('catalog.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
